import { metrics } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { getKvpsDealerId, type ThingName } from '@org-avp/device-models';
import { setupOtelMetricsProvider, shutdownMetrics } from '@org-avp/metrics';
import { getBrand, getMarket } from '@org-avp/models';
import { DIST_ENDPOINTS, decodePairingToken } from '@org-avp/naap-models';
import { KioskStorageSchema } from '@/lib/kioskStorage';
import * as pkg from '../../package.json';
import { STAGE } from './env';
import { logger } from './logger';

/**
 * Track the current device thingName to avoid redundant enrichment.
 * Set at startup if the device is already paired (cache hit), or after
 * enrichMetricsWithDevice() completes for first-time pairing.
 */
let currentThingName: ThingName | null = null;

const baseOptions = {
  namespace: 'naap',
  name: 'naap-kiosk',
  version: pkg.version,
  stage: STAGE,
  url: `${DIST_ENDPOINTS[STAGE].baseUrl}/v1/metrics`,
  apiKey: DIST_ENDPOINTS[STAGE].apiKey,
  consoleDebug: false,
} as const;

function createDeviceResource(thingName: ThingName) {
  const dealerId = getKvpsDealerId(thingName);
  return resourceFromAttributes({
    'service.instance.id': thingName,
    'avp.thingName': thingName,
    'avp.brand': getBrand(dealerId),
    'avp.market': getMarket(dealerId),
    'avp.dealerId': dealerId,
  });
}

/**
 * Read the cached thingName from localStorage synchronously using the
 * shared KioskStorageSchema for safe, validated parsing.
 *
 * localStorage is a synchronous browser API — safe to call before React mounts.
 *
 * @returns The cached ThingName, or null if not paired or on any parse error.
 */
function readCachedThingName(): ThingName | null {
  try {
    const raw = localStorage.getItem('kiosk');
    if (!raw) return null;
    const result = KioskStorageSchema.safeParse(raw);
    if (!result.success) return null;
    const { pairingToken } = result.data ?? {};
    if (!pairingToken) return null;
    return decodePairingToken(pairingToken).thingName;
  } catch {
    return null;
  }
}

/**
 * Initialize the metrics provider at app startup.
 *
 * If a pairingToken is cached in localStorage (device already paired), the
 * provider is created immediately with a sync Resource including all device
 * attributes — no enrichment step needed later.
 *
 * If not paired, the provider is created without device attributes. Metrics
 * start exporting immediately; device attributes are added by
 * enrichMetricsWithDevice() once pairing completes.
 *
 * Must be called once from bootstrap.tsx. The returned promise resolves when
 * the MeterProvider is globally registered.
 */
export function initMetrics(): Promise<void> {
  const cachedThingName = readCachedThingName();

  const promise = cachedThingName
    ? setupOtelMetricsProvider({ ...baseOptions, resource: createDeviceResource(cachedThingName) })
    : setupOtelMetricsProvider(baseOptions);

  if (cachedThingName) {
    currentThingName = cachedThingName;
  }

  return promise;
}

/**
 * Enrich the metrics provider with device-specific resource attributes.
 *
 * Idempotent: if the provider already reflects the given thingName (set at
 * startup from localStorage cache, or by a previous call), this is a no-op.
 * Handles re-pairing within the same session: if the thingName changes,
 * the provider is shut down and recreated with the new device Resource.
 *
 * @param thingName - The device thing name obtained after pairing completes.
 */
export async function enrichMetricsWithDevice(thingName: ThingName): Promise<void> {
  if (currentThingName === thingName) return;

  currentThingName = thingName;

  try {
    await shutdownMetrics();
    metrics.disable();
    await setupOtelMetricsProvider({ ...baseOptions, resource: createDeviceResource(thingName) });
    logger.info('Metrics enriched with device context');
  } catch (error: unknown) {
    logger.withError(error).error('Failed to enrich metrics with device context');
    currentThingName = null;
  }
}
